import { useLocation } from 'react-router';
import { useCallback, useContext, useMemo } from 'react';
import dayjs from 'dayjs';
import { store } from 'components/Store';
import parliamentarianUsers from 'components/Dashboard/SidebarElements/parliamentarianUsers';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import useWindowHeight from 'components/Dashboard/utilities/useWindowHeight';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import useDashboardUrlCondition from 'components/Dashboard/Navigation/useDashboardUrlCondition';
import useResearchItemsUrl from 'components/Dashboard/Navigation/useResearchItemsUrl';
import getAppSite from 'utils/getAppSite';
import { useAuth0 } from '@auth0/auth0-react';
import { dataHubUsers } from 'components/Dashboard/SidebarElements/parliamentarianUsers';

// The full-access id order per plan family. Links whose feature isn't enabled on the current
// plan (link.disabled, e.g. analytics_link/reports_link) sink to the end at render time - see
// the stable sort in linksToRender - so there's no separate array per access state: whichever
// links are disabled automatically end up last, in their normal declared order relative to
// each other.
export const LINK_ORDER_BY_PLAN = {
  standard: [
    'dashboard_link',
    'monitor_link',
    'campaigns_link',
    'stakeholder_mapping_link',
    'parliamentarians_link',
    'analytics_link',
    'reports_link',
    'brief_policymakers_link',
    'ai_assistant_link',
    'manifesto_tracker__link',
    'calendar_link',
  ],
  parliamentarian: [
    'dashboard_link',
    'monitor_link',
    'ai_assistant_link',
    'campaigns_link',
    'stakeholder_mapping_link',
    'parliamentarians_link',
    'reports_link',
    'analytics_link',
    'brief_policymakers_link',
    'manifesto_tracker__link',
    'calendar_link',
  ],
  newAgencyPlan: [
    'dashboard_link',
    'monitor_link',
    'campaigns_link',
    'stakeholder_mapping_link',
    'parliamentarians_link',
    'analytics_link',
    'brief_policymakers_link',
    'ai_assistant_link',
    'reports_link',
    'manifesto_tracker__link',
    'calendar_link',
  ],
  dataHub: ['analytics_link', 'public_affairs_suite_link'],
};

const useNavSections = ({
  linksContainerRef,
  navPagesRef,
  actionsElementRef,
  onShowAnalyticsPopup,
  onShowReportsPopup,
}) => {
  const globalState = useContext(store);
  const { dispatch, state } = globalState;
  const { activePlan, selectedCrmContacts, selectedGroups } = state ?? {};
  const { code, enableAnalytics, enableReports, teamName } = activePlan ?? {};
  const parliamentarianUser = parliamentarianUsers(code);
  const { user } = useAuth0();
  const dataHubUser = dataHubUsers(activePlan);
  const { agencyUser, newAgencyPlan } = useTopicOrClientWord();
  const { pathname: locationPathname, search } = useLocation();
  const { windowWidth } = useWindowHeight();
  const isMobile = useIsMobile();
  const { allowedDashboardUsers } = useDashboardUrlCondition();
  const { stakeholderUrl, policymakerUrl, monitorUrl } = useResearchItemsUrl();

  const linkClickHandler = useCallback(() => {
    const hamburgerMenu = document.getElementsByClassName('hamburger')[0];
    hamburgerMenu?.classList.remove('is-active');
    dispatch({ type: 'SHOW_HIDE_MENU', value: true });
  }, [dispatch]);

  const peopleLinkClickHandler = useCallback(
    (event) => {
      linkClickHandler();
      if (locationPathname === '/people/directory') {
        event.preventDefault();
        dispatch({ type: 'MODIFY_SECTION', parameter: 'crmContactsTableOnSort', value: { sort: '' } });
      }
    },
    [linkClickHandler, locationPathname, dispatch]
  );

  const hideInfluenceSection =
    parliamentarianUser || (teamName === 'NCA' && user?.email !== 'james.mitchell@nca.gov.uk');

  const isContactsSelected =
    selectedCrmContacts?.selectedIds?.length > 0 ||
    selectedCrmContacts?.selectedAll ||
    selectedCrmContacts?.selectedAllVisible?.length > 0;

  const isGroupsSelected = selectedGroups?.selectedIds?.length > 0 || selectedGroups?.selectedAll;
  const activeSubNavCondition = isContactsSelected || isGroupsSelected;

  const links = useMemo(
    () => [
      {
        id: 'dashboard_link',
        label: 'Dashboard',
        listClassname: 'text-align-center',
        to: '/dashboard',
        className: ({ isActive }) =>
          `${isActive ? 'active-page' : ''} static-page-monitor topnav-monitor-link static-page-link`,
        'data-intercom-target': 'Dashboard link',
        onClick: linkClickHandler,
        condition: allowedDashboardUsers,
        pathname: 'dashboard',
      },
      {
        id: 'monitor_link',
        label: 'Monitor',
        listClassname: 'text-align-center',
        to: monitorUrl,
        className: ({ isActive }) =>
          `${isActive ? 'active-page' : ''} static-page-monitor topnav-monitor-link static-page-link ${
            locationPathname.includes('/monitor-inbox') ? 'active-page ' : ''
          }`,
        'data-intercom-target': 'Monitor link',
        onClick: linkClickHandler,
        condition: true,
        pathname: 'monitor-inbox',
      },
      {
        id: 'campaigns_link',
        label: 'Campaigns',
        listClassname: 'text-align-center',
        to: '/campaigns',
        className: ({ isActive }) =>
          `${isActive ? 'active-page' : ''} topnav-monitor-link static-page-link campaigns-page`,
        'data-intercom-target': 'Campaigns link',
        onClick: linkClickHandler,
        condition: parliamentarianUser,
        pathname: 'campaigns',
      },
      {
        id: 'stakeholder_mapping_link',
        label: 'People',
        listClassname: 'text-align-center',
        to: '/people/directory',
        className: `static-page-link topnav-influence-link ${
          locationPathname.includes('/people')
            ? `${activeSubNavCondition ? 'active-page-active-subnav' : 'active-page'}`
            : ''
        }`,
        onClick: peopleLinkClickHandler,
        'data-intercom-target': 'Influence link',
        condition: !hideInfluenceSection,
        pathname: 'people/directory',
      },
      {
        id: 'parliamentarians_link',
        label: 'Policymakers by topic',
        listClassname: 'text-align-center',
        to: parliamentarianUser ? policymakerUrl : stakeholderUrl,
        className: ({ isActive }) => `${isActive ? 'active-page' : ''} static-page-link topnav-influence-link`,
        onClick: linkClickHandler,
        'data-intercom-target': 'Parliamentarian stakeholder mapping link',
        condition: parliamentarianUser,
        pathname: parliamentarianUser ? 'policymakers' : 'people/stakeholder-mapping',
      },
      {
        id: 'analytics_link',
        label: dataHubUser ? 'Data Hub' : 'Analytics',
        listClassname: `text-align-center ${newAgencyPlan ? '' : 'static-page-link-analytics-container'}`,
        to: `/analytics/dashboard${
          getUrlParam('topic-id') && getUrlParam('topic-id') !== 'all'
            ? `?topic-id=${getUrlParam('topic-id')}`
            : `${
                getUrlParam('search') &&
                (locationPathname.startsWith('/analytics') || locationPathname.startsWith('/data-hub'))
                  ? search
                  : ''
              }`
        }`,
        className: ({ isActive }) =>
          `${isActive ? 'active-page' : ''} static-page-link topnav-influence-link ${
            newAgencyPlan ? '' : 'analytics-static-link'
          } ${
            locationPathname.match(/\/(analytics|data-hub)\/[\w-]+/i)?.input || locationPathname === '/data-hub'
              ? 'active-page'
              : ''
          }`,
        onClick: (e) => {
          if (!enableAnalytics) {
            e.preventDefault();
            onShowAnalyticsPopup?.();
          }
          linkClickHandler();
        },
        'data-intercom-target': 'Analytics page',
        condition: true,
        disabled: !enableAnalytics,
        pathname: 'analytics/dashboard',
      },
      {
        id: 'reports_link',
        label: 'Report builder',
        listClassname: 'text-align-center static-page-link-reports-container d-none d-lg-block',
        to: '/reports',
        className: `static-page-link topnav-influence-link reports-static-link ${
          locationPathname.includes('/reports') ? 'active-page' : ''
        } d-none d-lg-block`,
        onClick: (e) => {
          if (!enableReports) {
            e.preventDefault();
            onShowReportsPopup?.();
          }
          linkClickHandler();
        },
        'data-intercom-target': 'Reports link',
        condition: !agencyUser,
        disabled: !enableReports,
        pathname: 'reports',
      },
      {
        id: 'public_affairs_suite_link',
        label: 'Public Affairs Suite',
        listClassname: 'text-align-center',
        to: '/public-affairs-suite',
        className: 'static-page-link topnav-influence-link',
        onClick: (e) => {
          e.preventDefault();
          dispatch({
            type: 'MODIFY_SECTION',
            parameter: 'dataHubNavigationPopup',
            value: { isOpen: true, allowClose: true },
          });
          linkClickHandler();
        },
        'data-intercom-target': 'Public Affairs Suite link',
        condition: dataHubUser,
        disabled: true,
        pathname: 'public-affairs-suite',
      },
      {
        id: 'brief_policymakers_link',
        label: 'Brief policymakers',
        listClassname: 'text-align-center',
        to: '/brief-policymakers',
        className: `static-page-link topnav-influence-link ${
          locationPathname.includes('/brief-policymakers') ? 'active-page' : ''
        }`,
        onClick: linkClickHandler,
        'data-intercom-target': 'Brief policymakers link',
        condition: !parliamentarianUser && getAppSite() !== 'usa',
        pathname: 'brief-policymakers',
      },
      {
        id: 'ai_assistant_link',
        label: 'AI assistant',
        listClassname: 'text-align-center',
        to: '/ai-assistant',
        className: `static-page-link topnav-influence-link ${
          locationPathname.match(/\/ai-assistant\/content\/\w+/i)?.input ? 'ai-assistant-content' : ''
        } ${locationPathname.includes('/ai-assistant') ? 'active-page' : ''}`,
        onClick: linkClickHandler,
        'data-intercom-target': 'Writing assistant link',
        condition: parliamentarianUser,
        pathname: 'ai-assistant',
      },
      {
        id: 'manifesto_tracker__link',
        label: 'Devolved elections',
        listClassname: 'text-align-center',
        to: '/devolved-elections-2026/manifestos',
        className: `static-page-link topnav-influence-link ${
          locationPathname.includes('/devolved-elections-2026/manifestos') ? 'active-page' : ''
        }`,
        'data-intercom-target': 'Devolved election manifesto tracker link',
        pathname: 'devolved-elections-2026/manifestos',
        condition: false, //AE: In case we need this feature in the future again
      },
      {
        id: 'calendar_link',
        label: 'Calendar',
        listClassname: 'text-align-center',
        to: `/calendar/${dayjs().format('YYYY-MM-DD')}`,
        className: ({ isActive }) => `${isActive ? 'active-page' : ''} static-page-link topnav-calendar-link`,
        'data-intercom-target': 'Calendar link',
        onClick: linkClickHandler,
        condition: isMobile && !newAgencyPlan && getAppSite() !== 'usa',
        pathname: 'calendar',
      },
    ],
    [
      linkClickHandler,
      peopleLinkClickHandler,
      allowedDashboardUsers,
      monitorUrl,
      locationPathname,
      parliamentarianUser,
      stakeholderUrl,
      policymakerUrl,
      activeSubNavCondition,
      hideInfluenceSection,
      newAgencyPlan,
      enableAnalytics,
      enableReports,
      agencyUser,
      isMobile,
      onShowAnalyticsPopup,
      onShowReportsPopup,
      search,
      dataHubUser,
    ]
  );

  const linksToRender = useMemo(() => {
    let planKey;
    switch (true) {
      case dataHubUser:
        planKey = 'dataHub';
        break;
      case parliamentarianUser:
        planKey = 'parliamentarian';
        break;
      case newAgencyPlan:
        planKey = 'newAgencyPlan';
        break;
      default:
        planKey = 'standard';
    }

    const linksById = new Map(links.map((link) => [link.id, link]));
    return LINK_ORDER_BY_PLAN[planKey]
      .map((id) => linksById.get(id))
      .filter((link) => link.condition || typeof link.condition === 'undefined')
      .sort((a, b) => (a.disabled ? 1 : 0) - (b.disabled ? 1 : 0));
  }, [links, enableAnalytics, enableReports, parliamentarianUser, newAgencyPlan, dataHubUser]);

  const { moreLinks, urls } = useMemo(() => {
    const itemsResult = [];
    const urlsResult = [];
    const linksContainer = linksContainerRef?.current;
    const currentlyHiddenItems = linksContainer
      ? [...linksContainer.querySelectorAll('li')].filter((item) => item?.offsetTop > 0)
      : [];

    linksToRender?.forEach((link) => {
      const { label, onClick, disabled, ...linkProps } = link;
      const linkElement = linksContainer?.querySelector(`#${linkProps.id}`);
      const offsetAdjustment = Math.ceil((linkElement?.parentElement?.offsetHeight - linkElement?.offsetHeight) / 2);

      if (linkElement?.offsetTop > offsetAdjustment) {
        const lastItemFitsCondition =
          currentlyHiddenItems?.length === 1 &&
          !!currentlyHiddenItems?.[0]?.querySelector(`#${linkProps.id}`) &&
          actionsElementRef?.current &&
          linkElement?.offsetWidth <= 116;

        const validateActiveClass =
          ((linkProps.to.startsWith('/analytics/dashboard') || linkProps.to.startsWith('/data-hub/dashboard')) &&
            /^\/(analytics|data-hub)\/[\w-]+/.test(locationPathname)) ||
          linkProps.to?.startsWith(locationPathname) ||
          locationPathname.startsWith(linkProps.to);

        if (lastItemFitsCondition && navPagesRef.current) {
          navPagesRef.current.style.paddingRight = 0;
        } else {
          itemsResult.push({
            linkProps,
            label,
            disabled,
            onClick,
            key: `item-${link?.id}`,
            className: `w-100 d-block text-left general-button list-action-button px-3 py-2 reset-link reset-link-color text-main-blue ${
              validateActiveClass ? 'active' : ''
            }`,
          });
          urlsResult.push(linkProps.to);
        }
      }
    });

    return { moreLinks: itemsResult, urls: urlsResult };
  }, [linksToRender, linksContainerRef, navPagesRef, actionsElementRef, windowWidth, locationPathname]);

  return { linksToRender, moreLinks, urls };
};

export default useNavSections;
